<template>
  <CCard class="setting-card auth-policy-card">
    <CCardHeader>
      <div class="auth-policy-heading">
        <div class="section-heading">
          <span class="section-icon"><CIcon name="cil-lock-locked"/></span>
          <div>
            <strong>ログイン方法</strong>
            <p>利用者がどの方法でログインできるかを決めます。</p>
            <!--
              **「保存済み」だけでは答えになっていない。**
              見に来る理由は「うちは何が有効なのか」。開かないと
              分からない状態にしない。
            -->
            <ul v-if="compact && policyLoaded" class="setting-values">
              <li
                v-for="one in enabledMethods"
                :key="one"
                class="setting-values__chip"
              >{{ one }}</li>
              <li
                v-if="enabledMethods.length === 0"
                class="setting-values__chip setting-values__chip--warn"
              >入れる方法がありません</li>
            </ul>
            <p v-else-if="compact && !loading" class="setting-values__note">
              現在の設定を読み込めませんでした。
            </p>
          </div>
        </div>
        <CBadge :color="dirty ? 'warning' : 'success'">
          {{ dirty ? '未保存の変更あり' : '保存済み' }}
        </CBadge>
      <SettingSectionAction
        v-if="compact"
        :to="{ name: '自社設定のログイン方法' }"
      />
      </div>
    </CCardHeader>

    <CCardBody v-if="!compact">
      <!-- 上端に出すのは読み込みの失敗だけ。保存の結果は［保存］のすぐ上に出す。 -->
      <CAlert
        v-if="errorMessage"
        color="danger"
        close-button
        @update:show="errorMessage = ''"
      >
        {{ errorMessage }}
      </CAlert>

      <div v-if="loading" class="auth-policy-loading">
        <CSpinner color="primary"/>
        <span>ログイン方法の設定を読み込んでいます…</span>
      </div>

      <!--
        読めなかったときは入力欄を出さない。**出すと、既定値（パスワードだけ）が
        入った状態で保存でき、いま開いているパスキーやGoogleをまとめて
        閉じてしまう。**読めていない設定を上書きさせない。
      -->
      <div v-else-if="!policyLoaded" class="auth-policy-load-failed">
        <CIcon name="cil-warning" size="2xl"/>
        <strong>ログイン方法の設定を表示できませんでした。</strong>
        <p>
          通信状態と権限を確認して、もう一度読み込んでください。
          現在の状態を読み込めていないまま保存すると、現在利用できているログイン方法が意図せず無効になる恐れがあるため、ここでは編集できません。
        </p>
        <CButton color="primary" variant="outline" :disabled="loading" @click="loadPolicy">
          再読み込み
        </CButton>
      </div>

      <template v-else>
        <CAlert color="info">
          ここで閉じた方法は、次のログインから使えなくなります。すでにログインしている人がその場で追い出されることはありません。
        </CAlert>

        <p v-if="allowedDomainsRequired" class="preika-required-note">
          <span class="preika-required-note__mark" aria-hidden="true">*</span>
          <span>が付いている項目は必須入力項目です</span>
        </p>

        <section class="auth-method-list" aria-labelledby="auth-policy-methods">
          <h3 id="auth-policy-methods" class="auth-policy-subheading">入れる方法</h3>

          <div class="auth-method">
            <div class="auth-method__row">
              <label class="custom-switch mb-0">
                <input
                  id="authPolicyPassword"
                  v-model="form.passwordLoginEnabled"
                  type="checkbox"
                  class="custom-switch-input"
                  :disabled="saving"
                >
                <span class="custom-switch-indicator" aria-hidden="true"></span>
              </label>
              <div class="auth-method__text">
                <label for="authPolicyPassword" class="auth-method__label">パスワードで入れる</label>
                <p>ログインE-Mailとパスワードでログインします。</p>
              </div>
            </div>
            <!--
              **切った瞬間に締め出しが起きうる切り替え。**何が起きるかを、
              押す前に同じ場所で出す。あとから問い合わせで気づくのでは遅い。
            -->
            <CAlert v-if="!form.passwordLoginEnabled" color="warning" class="auth-method__warning">
              パスワードを無効にすると、<b>パスキーの登録も外部アカウントの連携も
              していない方はログインできなくなります。</b>
              対象の方に登録を完了していただいてから保存してください。
            </CAlert>
          </div>

          <div class="auth-method">
            <div class="auth-method__row">
              <label class="custom-switch mb-0">
                <input
                  id="authPolicyPasskey"
                  v-model="form.passkeyEnabled"
                  type="checkbox"
                  class="custom-switch-input"
                  :disabled="saving"
                >
                <span class="custom-switch-indicator" aria-hidden="true"></span>
              </label>
              <div class="auth-method__text">
                <label for="authPolicyPasskey" class="auth-method__label">パスキーを使う</label>
                <!--
                  パスキーに「許すドメイン」のような設定は要らない。**自分の端末の
                  鍵を自分の口座へ登録するだけ**で、他社のドメインが混ざる余地が無い。
                  Googleと並べると同じ設定が要りそうに見えるので、その場で断っておく。
                -->
                <p>
                  端末の指紋・顔認証・PINでログインします。
                  登録するのは本人の端末の鍵だけなので、Googleのような許可ドメインの設定は必要ありません。
                </p>
              </div>
            </div>
          </div>

          <div class="auth-method">
            <div class="auth-method__row">
              <label class="custom-switch mb-0">
                <input
                  id="authPolicyGoogle"
                  v-model="form.googleLoginEnabled"
                  type="checkbox"
                  class="custom-switch-input"
                  :disabled="saving"
                >
                <span class="custom-switch-indicator" aria-hidden="true"></span>
              </label>
              <div class="auth-method__text">
                <label for="authPolicyGoogle" class="auth-method__label">Google でログイン</label>
                <p>Google Workspace のアカウントでログインします。個人のGoogleアカウントではログインできません。</p>
              </div>
            </div>
          </div>

          <div class="auth-method auth-method--pending">
            <div class="auth-method__row">
              <label class="custom-switch mb-0">
                <!--
                  **押せなくしてあるが、値は捨てない。**この画面は部分更新をしない
                  約束なので、送らなかった項目はサーバで消える。読んだ値をそのまま
                  返すために、v-model ではなく :checked で見せるだけにしている。
                -->
                <input
                  id="authPolicyMicrosoft"
                  type="checkbox"
                  class="custom-switch-input"
                  :checked="form.microsoftLoginEnabled"
                  disabled
                >
                <span class="custom-switch-indicator" aria-hidden="true"></span>
              </label>
              <div class="auth-method__text">
                <label for="authPolicyMicrosoft" class="auth-method__label">
                  Microsoft でログイン
                  <CBadge color="secondary" class="auth-method__badge">未提供</CBadge>
                </label>
                <p>
                  現在ご利用いただけません。
                  ご利用をご検討の場合は、サポートへお問い合わせください。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section class="auth-google-link" aria-labelledby="auth-policy-google-link">
          <h3 id="auth-policy-google-link" class="auth-policy-subheading">Google アカウントの連携</h3>

          <div class="auth-method">
            <div class="auth-method__row">
              <label class="custom-switch mb-0">
                <input
                  id="authPolicyGoogleSelfService"
                  v-model="form.googleLinkSelfServiceEnabled"
                  type="checkbox"
                  class="custom-switch-input"
                  :disabled="saving"
                >
                <span class="custom-switch-indicator" aria-hidden="true"></span>
              </label>
              <div class="auth-method__text">
                <label for="authPolicyGoogleSelfService" class="auth-method__label">
                  利用者による Google アカウントの連携を許可する
                </label>
                <p>
                  利用者が右上のメニューの「{{ securityScreenName }}」から、
                  自分のGoogleアカウントを連携できるようにします。
                </p>
                <!--
                  **行き先は名前で言う。**ここは以前「個人設定から」と
                  案内していたが、その画面に連携の設定は無い。
                  開いた管理者は右上から個人設定を選び、何も見つけられなかった。
                -->
              </div>
            </div>
            <!--
              **開けた側が危ない切り替え。**許すドメインを空にしたまま開けると、
              社員が副業先や自分で取ったドメインのWorkspaceを結び付けられ、
              そのアカウントでPREIKAへ入れてしまう。だからこのフラグを開けている間だけ
              ドメインを必須にしている（`allowedDomainsRequired`）。
            -->
            <CAlert v-if="form.googleLinkSelfServiceEnabled" color="warning" class="auth-method__warning">
              連携を許可するドメインを空欄のまま有効にすると、
              <b>利用者は自社以外のGoogle Workspaceアカウントも連携できるようになります。</b>
              有効にする場合は、下記の「連携を許可するドメイン」を必ず入力してください。
            </CAlert>
          </div>

          <div class="auth-domain-field">
            <label for="authPolicyGoogleDomains" class="auth-method__label">
              連携を許可するドメイン<span v-if="allowedDomainsRequired" class="asterisk">*</span>
            </label>
            <input
              id="authPolicyGoogleDomains"
              v-model="form.googleAllowedDomains"
              type="text"
              class="form-control"
              placeholder="example.co.jp,example-group.co.jp"
              :disabled="saving"
            >
            <small>
              複数指定する場合はカンマ区切りで入力します。
              <b>空欄の場合、すべてのGoogle Workspaceドメインからの連携を許可します。</b>
              個人のGoogleアカウントは、この設定にかかわらず連携できません。
              「利用者による Google アカウントの連携を許可する」がONの間は必須です。
            </small>
            <p v-if="normalizedDomains.length" class="auth-domain-preview">
              保存内容：<code>{{ normalizedDomains.join(',') }}</code>
            </p>
          </div>
        </section>

        <CAlert v-if="missingFields.length" color="warning" class="mb-3">
          <b>あと{{ missingFields.length }}項目直すと、保存できます。</b>
          <ul class="mb-0 mt-2">
            <li v-for="what in missingFields" :key="what">{{ what }}</li>
          </ul>
        </CAlert>

        <ActionResultMessage :text="saveMessage.text" :type="saveMessage.type" />
        <div class="auth-policy-actions">
          <!--
            **押せない条件は、上の一覧（missingFields）と同じ computed を見る。**
            片方だけ直すと「あと0つです」と出ているのに押せない、という
            さらに分かりにくい形になる。

            **「変更が無いから押せない」は作らない。**同じ内容をもう一度
            保存しても結果は変わらないので、止める理由が無い。止めると、
            画面に出す理由がひとつ増えるだけになる。
          -->
          <CButton
            class="auth-policy-save"
            color="primary"
            :disabled="saving || noUsableSignInMethod || linkWithoutGoogleLogin || allowedDomainsRequiredButEmpty || allowedDomainsTooLong || malformedDomains.length > 0"
            @click="save"
          >
            {{ saving ? '保存しています' : 'ログイン方法を保存' }}
          </CButton>
        </div>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import SettingSectionAction from './SettingSectionAction.vue'
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import api from '@/services/authPolicySettings'
import { SECURITY_SCREEN_NAME } from '@/common/screenNames'

/**
 * ドメイン1つぶんの綴り。**列の CHECK と同じ形にする。**
 *
 * サーバは `hd`（Workspace のドメイン）と**完全一致**で照合する。
 * だからここへ入れてよいのは `aki-corp.com` のようなドメインだけで、
 * `user@aki-corp.com` や `https://aki-corp.com` を入れると、
 * **どの組織にも一致しない＝全員が落ちる**設定が黙って保存される。
 * 保存できてしまってから「誰も入れない」と気づくのがいちばん高い。
 *
 * ## 緩くしても厳しくしても事故になる
 *
 * 緩いと、画面が通したものを列の CHECK
 * （`ck_account_auth_policy_google_domains`）が落とす。**利用者に出るのは
 * 「システムエラー」だけで、どこを直せばよいのか一言も出ない。**
 *
 * 厳しいと、逆に**正しいドメインを画面が断る。**ここは前まで末尾を
 * `[a-z]{2,63}` に縛っていて、punycode のTLD
 * （`example.xn--q9jyb4c` ＝ `.みんな`）を持つ会社が画面からは永久に
 * 設定できなかった。**画面から触れるようにしたのに、その会社ぶんだけ
 * また本番へSQLを流すことになる。**しかも断り文句は「形が正しくありません」
 * なので、利用者は正しい字面を疑って直しようがない。
 *
 * だから back の `GoogleAllowedDomains.ONE_DOMAIN` と、列の CHECK と、
 * **この3つが同じ形であること自体が約束。**片方だけ直さない。
 */
const DOMAIN_PATTERN =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/

/**
 * 列は `VARCHAR(1000)`。
 *
 * **越えたぶんを切らない。**切ると、書いたはずのドメインが黙って消え、
 * その会社の社員だけが結び付けられなくなる。押す前に止める。
 */
const MAX_DOMAINS_LENGTH = 1000

/**
 * 設定行が無い会社の見え方。
 *
 * サーバ側 `AccountAuthPolicyEntity.defaultPolicy` と同じ「パスワードだけ」。
 * **ここを「全部オフ」にすると、読めなかったときに全部閉じた形が
 * 画面へ出る。**読み込みに失敗した場合は、そもそも入力欄を出さない。
 */
const emptyPolicy = () => ({
  passwordLoginEnabled: true,
  passkeyEnabled: false,
  googleLoginEnabled: false,
  googleLinkSelfServiceEnabled: false,
  googleAllowedDomains: '',
  microsoftLoginEnabled: false
})

/** カンマ区切りの入力を、書かれたままの見た目で1つずつに分ける。 */
function splitDomains (text) {
  return String(text == null ? '' : text)
    .split(',')
    .map(entry => entry.trim())
    .filter(entry => entry !== '')
}

/**
 * 送る形へ整える。小文字にして重複を落とす。
 *
 * サーバは照合のたびに小文字へ直すので、`AKI-CORP.COM` でも動く。
 * それでも整えてから送るのは、**表に入っている字面を人が読むから**。
 * 同じドメインが大文字違いで2行並んでいると、片方を消したときに
 * 消えていないように見える。
 */
function normalizeDomains (text) {
  const kept = []
  splitDomains(text).forEach(entry => {
    const domain = entry.toLowerCase()
    if (kept.indexOf(domain) < 0) kept.push(domain)
  })
  return kept
}

/**
 * 返ってきたものが、この口の形か。
 *
 * 保存の口は**保存できた値をそのまま返す**約束になっている
 * （back の `AccountAuthPolicyForm`）。ただし「返らなくなった」ときに
 * 画面が固まるのは困るので、形を見て、違えば送った内容へ落とす。
 * フラグを1つ見れば足りる——空の応答や `'success'` のような文字列と
 * 見分けたいだけで、中身の正しさを判定したいわけではない。
 */
function isPolicyShape (value) {
  return Boolean(value) && typeof value === 'object' &&
    typeof value.passwordLoginEnabled === 'boolean'
}

/**
 * サーバへ送る6つ。**画面で変えられない項目も必ず入れる。**
 *
 * この口は部分更新にしていない。送らなかった項目は「変えない」ではなく
 * **消える**ので、Microsoft のフラグも読んだ値をそのまま返す。
 */
function toPayload (policy) {
  return {
    passwordLoginEnabled: Boolean(policy.passwordLoginEnabled),
    passkeyEnabled: Boolean(policy.passkeyEnabled),
    googleLoginEnabled: Boolean(policy.googleLoginEnabled),
    googleLinkSelfServiceEnabled: Boolean(policy.googleLinkSelfServiceEnabled),
    googleAllowedDomains: normalizeDomains(policy.googleAllowedDomains).join(','),
    microsoftLoginEnabled: Boolean(policy.microsoftLoginEnabled)
  }
}

export default {
  name: 'AuthPolicySettings',

  components: { SettingSectionAction, ActionResultMessage },

  /**
   * 自社設定の一覧では **たたんで出す。**
   *
   * たたまないころは、この部品だけで縦 577〜1,389px を使っていて、
   * その中に自前の保存ボタンがあった。一覧に保存ボタンが散らばると、
   * どこを押せば何が決まるのか画面ごとに探すことになる。
   *
   * たたんだときは見出しと「変更」だけを出し、**中身も保存ボタンも
   * 出さない。**直すのは専用の画面で、決定はそこに1つだけ置く。
   */
  props: {
    compact: { type: Boolean, default: false }
  },
  data () {
    return {
      loading: true,
      saving: false,
      policyLoaded: false,
      // 読み込みの失敗だけ（画面の上端に出す）。
      errorMessage: '',
      // 保存の結果（押せなかった理由も）。［保存］のすぐ上に出す。
      saveMessage: { type: 'success', text: '' },
      form: emptyPolicy(),
      /** 最後にサーバと一致していた内容。未保存かどうかはこれと比べる。 */
      saved: toPayload(emptyPolicy())
    }
  },
  computed: {
    /**
     * いま入れる方法。<b>一覧はこれだけを出す。</b>
     *
     * 切り替えの説明や警告は編集画面の仕事で、一覧では邪魔になる。
     * ここで知りたいのは「何が開いているか」だけ。
     */
    enabledMethods () {
      const names = []
      if (this.form.passwordLoginEnabled) { names.push('パスワード') }
      if (this.form.passkeyEnabled) { names.push('パスキー') }
      if (this.form.googleLoginEnabled) { names.push('Google') }
      if (this.form.microsoftLoginEnabled) { names.push('Microsoft') }
      return names
    },

    /** 利用者が連携しに行く画面の名前。**メニューと同じものを出す。** */
    securityScreenName () {
      return SECURITY_SCREEN_NAME
    },

    /** 送る形のドメイン一覧。 */
    normalizedDomains () {
      return normalizeDomains(this.form.googleAllowedDomains)
    },
    /**
     * 綴りが違うもの。**書かれたままの字面で返す。**
     * 小文字へ直してから見せると、自分が打った行を探せない。
     */
    malformedDomains () {
      const bad = []
      splitDomains(this.form.googleAllowedDomains).forEach(entry => {
        if (DOMAIN_PATTERN.test(entry.toLowerCase())) return
        if (bad.indexOf(entry) < 0) bad.push(entry)
      })
      return bad
    },
    /**
     * ドメインが必須になるのはどんなときか。
     *
     * 空欄そのものは不正ではない（**空なら絞らない**のが仕様）。
     * ただし利用者が自分で後付けできる状態で空にすると、
     * 別の会社の Workspace を結び付けられる。**開けている間だけ必須**。
     */
    allowedDomainsRequired () {
      return Boolean(this.form.googleLinkSelfServiceEnabled)
    },
    allowedDomainsRequiredButEmpty () {
      return this.allowedDomainsRequired && this.normalizedDomains.length === 0
    },
    /**
     * 列に入りきらない長さ。
     *
     * **数えるのは整えたあとの字面。**入力欄には空白や重複が混ざるので、
     * 打った文字数で見ると、実際には収まるものを断ることになる。
     */
    allowedDomainsLength () {
      return this.normalizedDomains.join(',').length
    },
    allowedDomainsTooLong () {
      return this.allowedDomainsLength > MAX_DOMAINS_LENGTH
    },
    /** 後付けだけ開けても、Googleでログインできなければ使い道が無い。 */
    linkWithoutGoogleLogin () {
      return Boolean(this.form.googleLinkSelfServiceEnabled) &&
        !this.form.googleLoginEnabled
    },
    /**
     * 誰も入れない設定になっていないか。
     *
     * **Microsoft は数に入れない。**フラグは画面に出しているが、いまは
     * 1組織しか使えないので、開いていても入口として当てにできない。
     * 数に入れると「Microsoftが立っているから大丈夫」と読めてしまい、
     * 実際には誰も入れない設定が保存できる。
     */
    noUsableSignInMethod () {
      return !this.form.passwordLoginEnabled &&
        !this.form.passkeyEnabled &&
        !this.form.googleLoginEnabled
    },
    /** 送る内容。保存にも、未保存かどうかの判定にも同じものを使う。 */
    payloadToSend () {
      return toPayload(this.form)
    },
    dirty () {
      return JSON.stringify(this.payloadToSend) !== JSON.stringify(this.saved)
    },
    /**
     * 押せない理由の一覧。
     *
     * **保存ボタンの :disabled と、ここは同じ computed を見ている。**
     * 名前と書き方（`missingFields ()`）は他の入力画面と揃えてある。
     * **変えると、押せない理由を全画面まとめて見る検査が拾わなくなる。**
     */
    missingFields () {
      const missing = []
      if (this.noUsableSignInMethod) {
        missing.push('ログイン方法 — パスワード・パスキー・Google のいずれか1つは有効にしてください')
      }
      if (this.linkWithoutGoogleLogin) {
        missing.push('Google でログイン — 利用者による連携を許可する場合は、こちらもONにしてください')
      }
      if (this.allowedDomainsRequiredButEmpty) {
        missing.push('連携を許可するドメイン — 利用者による連携を許可する場合は必須です')
      }
      if (this.allowedDomainsTooLong) {
        missing.push('連携を許可するドメイン — ' + MAX_DOMAINS_LENGTH +
          '文字までです（整えると現在' + this.allowedDomainsLength + '文字）')
      }
      this.malformedDomains.forEach(entry => {
        missing.push('連携を許可するドメインの形式が正しくありません：' + entry)
      })
      return missing
    }
  },
  mounted () {
    this.loadPolicy()
  },
  methods: {
    async loadPolicy () {
      this.loading = true
      this.errorMessage = ''
      try {
        const result = await api.get()
        const policy = Object.assign(emptyPolicy(), result || {})
        /*
         * 入力欄には**サーバが持っている字面をそのまま**入れる。
         * 開いた瞬間に整形すると、何も触っていないのに「未保存の変更あり」
         * と出て、直したのか直していないのか分からなくなる。
         * 比べる相手（saved）は整形後なので、大文字小文字の違いだけなら
         * 未保存とは出ない。
         */
        this.form = Object.assign({}, policy, {
          googleAllowedDomains: String(policy.googleAllowedDomains || '')
        })
        this.saved = toPayload(policy)
        this.policyLoaded = true
      } catch (error) {
        this.policyLoaded = false
        this.errorMessage = this.extractError(
          error, 'ログイン方法の設定を取得できませんでした。')
      } finally {
        this.loading = false
      }
    },
    async save () {
      /*
       * 押せない状態なら、ここでも止める。**押せない条件は画面の都合で
       * 変わる**（ボタンを足す、条件を書き換える）ので、送る直前にも
       * 同じ一覧を見る。理由は一覧に出ているものと同じ言葉で出す。
       */
      if (this.missingFields.length) {
        this.saveMessage = { type: 'danger', text: this.missingFields[0] }
        return
      }
      const payload = this.payloadToSend
      if (!window.confirm(this.confirmMessage(payload))) return

      this.saving = true
      this.saveMessage = { type: 'success', text: '' }
      try {
        const stored = await api.update(payload)
        /*
         * 保存できた形を画面へ書き戻す。整形したドメインが入力欄に出るので、
         * **何が表に入ったのかを次に開かなくても確かめられる。**
         *
         * 書き戻すのは**サーバが返した値**であって、送った値ではない。
         * 同じ整形をこちらにも持っているが、2つが少しずれた日に、
         * **画面だけが「送ったつもりの字面」を表示し続ける。**表には別の
         * ものが入っていて、次に開くまで誰も気づけない。
         */
        const applied = isPolicyShape(stored) ? toPayload(stored) : payload
        this.form = Object.assign({}, this.form, applied)
        this.saved = Object.assign({}, applied)
        this.saveMessage = { type: 'success', text: 'ログイン方法を保存しました。次のログインから反映されます。' }
      } catch (error) {
        this.saveMessage = {
          type: 'danger',
          text: this.extractError(error, 'ログイン方法を保存できませんでした。')
        }
      } finally {
        this.saving = false
      }
    },
    /** 押す前に、フラグの名前ではなく「誰がどう入るか」を並べて見せる。 */
    confirmMessage (payload) {
      const usable = []
      if (payload.passwordLoginEnabled) usable.push('パスワード')
      if (payload.passkeyEnabled) usable.push('パスキー')
      if (payload.googleLoginEnabled) usable.push('Google')
      return 'この内容で保存しますか？\n\n' +
        'ログイン方法：' + usable.join('・') + '\n' +
        '利用者によるGoogle連携：' +
        (payload.googleLinkSelfServiceEnabled ? '許可する' : '許可しない') + '\n' +
        '連携を許可するドメイン：' +
        (payload.googleAllowedDomains || '制限なし')
    },
    onOff (value) {
      return value ? 'オン' : 'オフ'
    },
    /**
     * サーバが断った理由を、そのまま出す。
     *
     * サーバの文言は画面のラベルと同じ言葉で書いてある。ここで
     * 「保存できませんでした」に丸めると、**どの項目が悪いのかが消える。**
     */
    extractError (error, fallback) {
      const data = error && error.response && error.response.data
      if (typeof data === 'string' && data.trim()) return data.trim()
      const detail = data && data.message
      return detail || fallback
    }
  }
}
</script>

<style scoped>
.setting-card { min-width: 0; margin-bottom: 18px; }
/* 見出しの行を横いっぱいに。伸ばさないと「変更」が右端に来ない。 */
/*
  一覧に出す「いまの設定」。**状態（保存済み）と値を混ぜない。**
*/
.setting-values { display: flex; flex-wrap: wrap; gap: 6px; margin: 8px 0 0; padding: 0; list-style: none; }
.setting-values__chip { padding: 2px 10px; border-radius: 999px; background: #eef5fa; color: #28566f; font-size: 12px; line-height: 1.7; }
.setting-values__chip--warn { background: #fdf0e6; color: #8a4b20; font-weight: 700; }
.setting-values__note { margin: 8px 0 0; color: #8a6420; font-size: 12px; }

.auth-policy-heading { display: flex; width: 100%; align-items: flex-start; justify-content: space-between; gap: 16px; }
.section-heading { display: flex; align-items: flex-start; gap: 12px; }
.section-heading strong { color: #17384d; font-size: 16px; }
.section-heading p { margin: 3px 0 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }
.section-icon { display: inline-flex; width: 38px; height: 38px; flex: 0 0 auto; align-items: center; justify-content: center; border-radius: 12px; background: #e9f4fb; color: #0a6ca8; }
.auth-policy-loading { display: flex; align-items: center; justify-content: center; gap: 12px; min-height: 180px; color: #607884; }
.auth-policy-load-failed { display: flex; min-height: 210px; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #607884; text-align: center; }
.auth-policy-load-failed p { max-width: 560px; margin: 0 0 4px; line-height: 1.6; }
.auth-policy-subheading { margin: 0 0 10px; color: #314f62; font-size: 14px; font-weight: 800; }
.auth-method-list { margin-bottom: 22px; }
.auth-method { padding: 14px; border: 1px solid #dce8ef; border-radius: 12px; background: #fbfdfe; }
.auth-method + .auth-method { margin-top: 10px; }
.auth-method--pending { background: #f4f6f8; }
.auth-method__row { display: flex; align-items: flex-start; gap: 14px; }
.auth-method__text { min-width: 0; }
.auth-method__label { display: block; margin: 0 0 3px; color: #17384d; font-size: 14px; font-weight: 700; cursor: pointer; }
.auth-method__badge { margin-left: 8px; vertical-align: middle; }
.auth-method__text p { margin: 0; color: #6a7f8c; font-size: 12px; line-height: 1.7; overflow-wrap: anywhere; }
.auth-method__warning { margin: 12px 0 0; font-size: 12px; line-height: 1.7; }
.auth-google-link { margin-bottom: 18px; }
.auth-domain-field { margin-top: 12px; padding: 14px; border: 1px solid #dce8ef; border-radius: 12px; background: #fff; }
.auth-domain-field small { display: block; margin-top: 6px; color: #6a7f8c; font-size: 12px; line-height: 1.7; }
.auth-domain-preview { margin: 8px 0 0; color: #17384d; font-size: 12px; overflow-wrap: anywhere; }
.auth-domain-preview code { color: #0a6ca8; }
.auth-policy-actions { display: flex; justify-content: flex-end; }
.auth-policy-actions .btn { min-width: 200px; }

@media (max-width: 767.98px) {
  .auth-policy-heading { flex-direction: column; }
  .auth-policy-actions { justify-content: stretch; }
  .auth-policy-actions .btn { width: 100%; min-height: 44px; }
  .section-heading p { overflow-wrap: anywhere; }
}
</style>
